import React, { Fragment } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import DialogContentText from '@mui/material/DialogContentText'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Snackbar from '@mui/material/Snackbar'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import makeStyles from '@mui/styles/makeStyles'

import { useHistory } from 'react-router-dom'
import { useQuery } from 'react-query'

import { FirebaseContext } from '../../utils/firebase'
import 'firebase/auth'
import { useEnhancerPlatforms } from '../../contexts/EnhancerContext'

import { API_ROOT_URL } from '../../constants/'
import * as ROUTES from '../../constants/routes'

import appstleLogoImg from '../../media/appstle_subscriptions_logo.png'
import skioLogoImg from '../../media/skio_subscriptions_logo.png'
import hyrosLogoImg from '../../media/hyros_logo.png'
import loopLogoImg from '../../media/loop_subscriptions_logo.png'
import rechargeLogoImg from '../../media/recharge_logo.png'
import vidalyticsLogoImg from '../../media/vidalytics_logo.png'
import shopifyLogoImg from '../../media/shopify_logo.png'

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    left: 66,
    right: 0,
    width: 'calc(100% - 66px)',
    height: '100vh',
    overflowY: 'auto',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
  },
  paper: {
    padding: theme.spacing(2),
  },
  syncButton: {
    width: 100,
  },
  logoImg: {
    width: 50,
  },
}))

const Enhancers = () => {
  const classes = useStyles()
  const firebase = React.useContext(FirebaseContext)
  const history = useHistory()
  const { fetchEnhancerPlatforms } = useEnhancerPlatforms()

  const [showMessage, setShowMessage] = React.useState(false)
  const [message, setMessage] = React.useState('')
  const [messageSeverity, setMessageSeverity] = React.useState('success')

  // Appstle
  const [showAppstleDialog, setShowAppstleDialog] = React.useState(false)
  const [accountIdAppstle, setAccountIdAppstle] = React.useState('')
  const [accountNameAppstle, setAccountNameAppstle] = React.useState('')

  // Skio
  const [showSkioDialog, setShowSkioDialog] = React.useState(false)
  const [accountIdSkio, setAccountIdSkio] = React.useState('')
  const [accountNameSkio, setAccountNameSkio] = React.useState('')

  // Hyros
  const [showHyrosDialog, setShowHyrosDialog] = React.useState(false)
  const [accountIdHyros, setAccountIdHyros] = React.useState('')
  const [accountNameHyros, setAccountNameHyros] = React.useState('')
  const [apiKeyHyros, setApiKeyHyros] = React.useState('')

  // Loop
  const [showLoopDialog, setShowLoopDialog] = React.useState(false)
  const [accountIdLoop, setAccountIdLoop] = React.useState('')
  const [accountNameLoop, setAccountNameLoop] = React.useState('')
  const [apiKeyLoop, setApiKeyLoop] = React.useState('')

  // Recharge
  const [showRechargeDialog, setShowRechargeDialog] = React.useState(false)
  const [accountIdRecharge, setAccountIdRecharge] = React.useState('')
  const [accountNameRecharge, setAccountNameRecharge] = React.useState('')
  const [apiKeyRecharge, setApiKeyRecharge] = React.useState('')

  // Vidalytics
  const [showVidalyticsDialog, setShowVidalyticsDialog] = React.useState(false)
  const [apiKeyVidalytics, setApiKeyVidalytics] = React.useState('')
  // We connect from just the API key: the backend validates it against Vidalytics and pulls the
  // account id + name from the /account endpoint, so the user never types an account identifier.

  // Shopify Journey (no external account / API key — sourced from the linked Shopify integration)
  const [showShopifyJourneyDialog, setShowShopifyJourneyDialog] = React.useState(false)
  const [accountNameShopifyJourney, setAccountNameShopifyJourney] = React.useState('')

  const {
    isLoading: isLoadingEnhancers,
    data: enhancers = []
  } = useQuery(['enhancers-list'], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/enhancers`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      }).then(res => res.json())
    }),
    {
      cacheTime: 0,
      staleTime: 0,
      refetchOnWindowFocus: false,
    }
  )

  const { isLoading: isLoadingConnectAppstle, refetch: connectAppstle } = useQuery(['connect-appstle'], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/enhancers/appstle/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          accountId: accountIdAppstle,
          accountName: accountNameAppstle,
        })
      }).then(res => res.json())
    }),
    {
      enabled: false,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        setShowAppstleDialog(false)
        setAccountIdAppstle('')
        setAccountNameAppstle('')
        fetchEnhancerPlatforms()
        history.push(ROUTES.APPSTLE_ENHANCERS + `/${data.id}`)
      },
      onError: () => {
        setShowMessage(true)
        setMessage('Error connecting with Appstle. Please try again and contact support if error persists.')
        setMessageSeverity('error')
      }
    }
  )

  const { isLoading: isLoadingConnectSkio, refetch: connectSkio } = useQuery(['connect-skio'], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/enhancers/skio/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          accountId: accountIdSkio,
          accountName: accountNameSkio,
        })
      }).then(res => res.json())
    }),
    {
      enabled: false,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        setShowSkioDialog(false)
        setAccountIdSkio('')
        setAccountNameSkio('')
        fetchEnhancerPlatforms()
        history.push(ROUTES.SKIO_ENHANCERS + `/${data.id}`)
      },
      onError: () => {
        setShowMessage(true)
        setMessage('Error connecting with Skio. Please try again and contact support if error persists.')
        setMessageSeverity('error')
      }
    }
  )

  const { isLoading: isLoadingConnectHyros, refetch: connectHyros } = useQuery(['connect-hyros'], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/enhancers/hyros/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          accountId: accountIdHyros,
          accountName: accountNameHyros,
          apiKey: apiKeyHyros,
        })
      }).then(res => res.json())
    }),
    {
      enabled: false,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        setShowHyrosDialog(false)
        setAccountIdHyros('')
        setAccountNameHyros('')
        setApiKeyHyros('')
        fetchEnhancerPlatforms()
        history.push(ROUTES.HYROS_ENHANCERS + `/${data.id}`)
      },
      onError: () => {
        setShowMessage(true)
        setMessage('Error connecting with Hyros. Please try again and contact support if error persists.')
        setMessageSeverity('error')
      }
    }
  )

  const { isLoading: isLoadingConnectLoop, refetch: connectLoop } = useQuery(['connect-loop'], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/enhancers/loop/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          accountId: accountIdLoop,
          accountName: accountNameLoop,
          apiKey: apiKeyLoop,
        })
      }).then(res => res.json())
    }),
    {
      enabled: false,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        setShowLoopDialog(false)
        setAccountIdLoop('')
        setAccountNameLoop('')
        setApiKeyLoop('')
        fetchEnhancerPlatforms()
        history.push(ROUTES.LOOP_ENHANCERS + `/${data.id}`)
      },
      onError: () => {
        setShowMessage(true)
        setMessage('Error connecting with Loop. Please try again and contact support if error persists.')
        setMessageSeverity('error')
      }
    }
  )

  const { isLoading: isLoadingConnectRecharge, refetch: connectRecharge } = useQuery(['connect-recharge'], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/enhancers/recharge/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          accountId: accountIdRecharge,
          accountName: accountNameRecharge,
          apiKey: apiKeyRecharge,
        })
      }).then(res => res.json())
    }),
    {
      enabled: false,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        setShowRechargeDialog(false)
        setAccountIdRecharge('')
        setAccountNameRecharge('')
        setApiKeyRecharge('')
        fetchEnhancerPlatforms()
        history.push(ROUTES.RECHARGE_ENHANCERS + `/${data.id}`)
      },
      onError: () => {
        setShowMessage(true)
        setMessage('Error connecting with Recharge. Please try again and contact support if error persists.')
        setMessageSeverity('error')
      }
    }
  )

  const { isLoading: isLoadingConnectVidalytics, refetch: connectVidalytics } = useQuery(['connect-vidalytics'], () =>
    firebase.auth().currentUser.getIdToken(false).then(async token => {
      const res = await fetch(`${API_ROOT_URL}/api_fs/enhancers/vidalytics/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ apiKey: apiKeyVidalytics })
      })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(body.error || 'Could not connect to Vidalytics. Please try again.')
      return body
    }),
    {
      enabled: false,
      staleTime: Infinity,
      retry: false,  // a bad key is a definitive failure — surface it immediately, don't re-probe
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        setShowVidalyticsDialog(false)
        setApiKeyVidalytics('')
        fetchEnhancerPlatforms()
        history.push(ROUTES.VIDALYTICS_ENHANCERS + `/${data.id}`)
      },
      onError: (error) => {
        setShowMessage(true)
        setMessage((error && error.message) || 'Error connecting with Vidalytics. Please try again and contact support if error persists.')
        setMessageSeverity('error')
      }
    }
  )

  const { isLoading: isLoadingConnectShopifyJourney, refetch: connectShopifyJourney } = useQuery(['connect-shopify-journey'], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/enhancers/shopify_journey/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          accountName: accountNameShopifyJourney,
        })
      }).then(res => res.json())
    }),
    {
      enabled: false,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        setShowShopifyJourneyDialog(false)
        setAccountNameShopifyJourney('')
        fetchEnhancerPlatforms()
        history.push(ROUTES.SHOPIFY_JOURNEY_ENHANCERS + `/${data.id}`)
      },
      onError: () => {
        setShowMessage(true)
        setMessage('Error creating the Shopify Journey enhancer. Please try again and contact support if error persists.')
        setMessageSeverity('error')
      }
    }
  )

  const handleCloseMessage = (_, reason) => {
    if (reason === 'clickaway') return
    setShowMessage(false)
  }

  const handleCloseAppstleDialog = () => {
    setShowAppstleDialog(false)
    setAccountIdAppstle('')
    setAccountNameAppstle('')
  }

  const handleCloseSkioDialog = () => {
    setShowSkioDialog(false)
    setAccountIdSkio('')
    setAccountNameSkio('')
  }

  const handleCloseHyrosDialog = () => {
    setShowHyrosDialog(false)
    setAccountIdHyros('')
    setAccountIdHyros('')
    setApiKeyHyros('')
  }

  const handleCloseLoopDialog = () => {
    setShowLoopDialog(false)
    setAccountIdLoop('')
    setAccountNameLoop('')
    setApiKeyLoop('')
  }

  const handleCloseRechargeDialog = () => {
    setShowRechargeDialog(false)
    setAccountIdRecharge('')
    setAccountIdRecharge('')
    setApiKeyRecharge('')
  }

  const handleCloseVidalyticsDialog = () => {
    setShowVidalyticsDialog(false)
    setApiKeyVidalytics('')
  }

  const handleCloseShopifyJourneyDialog = () => {
    setShowShopifyJourneyDialog(false)
    setAccountNameShopifyJourney('')
  }

  return (
    <Container maxWidth='md'>

      {/* Appstle Enhancers */}
      <Box marginTop={2}>
        <Paper className={classes.paper}>
          <Typography variant='h5' align='center' gutterBottom>
            Appstle Enhancers
          </Typography>

          <List>
            {isLoadingEnhancers ?
              <Skeleton variant='rectangular' height={35} />
              : (enhancers.filter(e => e.platform === 'appstle_subscriptions').map(enhancer =>
                <Fragment key={enhancer.id}>
                  <ListItem
                    onClick={() => history.push(ROUTES.APPSTLE_ENHANCERS + `/${enhancer.id}`)}
                    button
                  >
                    <ListItemIcon>
                      <img
                        className={classes.logoImg}
                        alt='appstle_logo'
                        src={appstleLogoImg}
                      />
                    </ListItemIcon>
                    <ListItemText primary={enhancer.nickname} secondary={enhancer.platform_id} />
                  </ListItem>
                </Fragment>
              ))
            }
          </List>

          <Box marginTop={2}>
            <Button
              onClick={() => setShowAppstleDialog(true)}
              variant='contained'
              color='primary'
            >
              Connect
            </Button>
          </Box>
          <Dialog
            open={showAppstleDialog}
            onClose={handleCloseAppstleDialog}
          >
            <DialogTitle>
              Connect with Appstle Subscriptions
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                The <b>Account ID</b> is used as the ID of your Appstle Subscriptions acccount. You can find this in the URL when logged in to your Shopify store, then opening the Appstle Subscriptions app.
                In the sidebar, navigate to <b>Shop Settings</b>, then scroll down to the bottom to <b>Account Ownership Settings</b>. To the right, you will see <b>Access Permissions</b> followed by a number in parentheses.
                That number is your Account ID. Enter this value below. This value is unique to your Appstle account.
                <br />
                <br />
                The <b>Account Name</b> is a descriptive nickname that will be shown in LTV Numbers. You may choose to set this to the same name as your Shopify store, or any other name that helps you identify this Appstle account.
              </DialogContentText>
              <Box marginTop={2}>
                <TextField
                  label='Account ID'
                  name='appstle-account-id'
                  value={accountIdAppstle}
                  onChange={(event) => setAccountIdAppstle(event.target.value)}
                  variant='outlined'
                  size='small'
                  fullWidth
                  required
                />
              </Box>
              <Box marginTop={2}>
                <TextField
                  label='Account Name'
                  name='appstle-account-name'
                  value={accountNameAppstle}
                  onChange={(event) => setAccountNameAppstle(event.target.value)}
                  variant='outlined'
                  size='small'
                  fullWidth
                  required
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCloseAppstleDialog}
                color='error'
              >
                Cancel
              </Button>
              <Button
                onClick={() => connectAppstle()}
                color='secondary'
                disabled={!accountIdAppstle || !accountNameAppstle}
              >
                {isLoadingConnectAppstle ? <CircularProgress color='inherit' size={24} /> : 'Submit'}
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Box>

      {/* Skio Enhancers */}
      <Box marginTop={2}>
        <Paper className={classes.paper}>
          <Typography variant='h5' align='center' gutterBottom>
            Skio Enhancers
          </Typography>

          <List>
            {isLoadingEnhancers ?
              <Skeleton variant='rectangular' height={35} />
              : (enhancers.filter(e => e.platform === 'skio_subscriptions').map(enhancer =>
                <Fragment key={enhancer.id}>
                  <ListItem
                    onClick={() => history.push(ROUTES.SKIO_ENHANCERS + `/${enhancer.id}`)}
                    button
                  >
                    <ListItemIcon>
                      <img
                        className={classes.logoImg}
                        alt='skio_logo'
                        src={skioLogoImg}
                      />
                    </ListItemIcon>
                    <ListItemText primary={enhancer.nickname} secondary={enhancer.platform_id} />
                  </ListItem>
                </Fragment>
              ))
            }
          </List>

          <Box marginTop={2}>
            <Button
              onClick={() => setShowSkioDialog(true)}
              variant='contained'
              color='primary'
            >
              Connect
            </Button>
          </Box>
          <Dialog
            open={showSkioDialog}
            onClose={handleCloseSkioDialog}
          >
            <DialogTitle>
              Connect with Skio Subscriptions
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                The <b>Store Handle</b> identifies your Shopify store. You can find it in your Shopify admin URL: it is the value after <b>/store/</b>. For example, in <b>https://admin.shopify.com/store/revital8/orders</b> the Store Handle is <b>revital8</b>. Enter this value below.
                <br />
                <br />
                The <b>Account Name</b> is a descriptive nickname that will be shown in LTV Numbers. You may choose to set this to the same name as your Shopify store, or any other name that helps you identify this Skio account.
              </DialogContentText>
              <Box marginTop={2}>
                <TextField
                  label='Store Handle'
                  name='skio-store-handle'
                  value={accountIdSkio}
                  onChange={(event) => setAccountIdSkio(event.target.value)}
                  variant='outlined'
                  size='small'
                  fullWidth
                  required
                />
              </Box>
              <Box marginTop={2}>
                <TextField
                  label='Account Name'
                  name='skio-account-name'
                  value={accountNameSkio}
                  onChange={(event) => setAccountNameSkio(event.target.value)}
                  variant='outlined'
                  size='small'
                  fullWidth
                  required
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCloseSkioDialog}
                color='error'
              >
                Cancel
              </Button>
              <Button
                onClick={() => connectSkio()}
                color='secondary'
                disabled={!accountIdSkio || !accountNameSkio}
              >
                {isLoadingConnectSkio ? <CircularProgress color='inherit' size={24} /> : 'Submit'}
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Box>

      {/* Hyros Enhancers */}
      <Box marginTop={2}>
        <Paper className={classes.paper}>
          <Typography variant='h5' align='center' gutterBottom>
            Hyros Enhancers
          </Typography>

          <List>
            {isLoadingEnhancers ?
              <Skeleton variant='rectangular' height={35} />
              : (enhancers.filter(e => e.platform === 'hyros').map(enhancer =>
                <Fragment key={enhancer.id}>
                  <ListItem
                    onClick={() => history.push(ROUTES.HYROS_ENHANCERS + `/${enhancer.id}`)}
                    button
                  >
                    <ListItemIcon>
                      <img
                        className={classes.logoImg}
                        alt='hyros_logo'
                        src={hyrosLogoImg}
                      />
                    </ListItemIcon>
                    <ListItemText primary={enhancer.nickname} secondary={enhancer.platform_id} />
                  </ListItem>
                </Fragment>
              ))
            }
          </List>

          <Box marginTop={2}>
            <Button
              onClick={() => setShowHyrosDialog(true)}
              variant='contained'
              color='primary'
            >
              Connect
            </Button>
          </Box>
          <Dialog
            open={showHyrosDialog}
            onClose={handleCloseHyrosDialog}
          >
            <DialogTitle>
              Connect with Hyros
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                The <b>Master Email</b> is used as the ID of your Hyros acccount. This the email address that was used to create
                the Hyros account that is used as the master login for the account. Because this email can only
                be associated with a single account, it serves as a unique identifier for your account.
                <br />
                <br />
                The <b>Account Name</b> is the name of your Hyros acccount. This value can be seen in the sub-text
                below your profile name in the top right corner when logged in.
                <br />
                <br />
                The <b>API Key</b> is needed to connect with Hyros. You will first need to create an API Key in your Hyros account
                with roles of <b>Tracking</b> and <b>Sales Data</b> added to it. After creating the API Key, insert the value below.
              </DialogContentText>
              <Box marginTop={2}>
                <TextField
                  label='Master Email'
                  name='hyros-account-id'
                  value={accountIdHyros}
                  onChange={(event) => setAccountIdHyros(event.target.value)}
                  variant='outlined'
                  size='small'
                  fullWidth
                  required
                />
              </Box>
              <Box marginTop={2}>
                <TextField
                  label='Account Name'
                  name='hyros-account-name'
                  value={accountNameHyros}
                  onChange={(event) => setAccountNameHyros(event.target.value)}
                  variant='outlined'
                  size='small'
                  fullWidth
                  required
                />
              </Box>
              <Box marginTop={2}>
                <TextField
                  label='API Key'
                  name='hyros-api-key'
                  value={apiKeyHyros}
                  onChange={(event) => setApiKeyHyros(event.target.value)}
                  variant='outlined'
                  size='small'
                  fullWidth
                  required
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCloseHyrosDialog}
                color='error'
              >
                Cancel
              </Button>
              <Button
                onClick={() => connectHyros()}
                color='secondary'
                disabled={!accountIdHyros || !accountNameHyros || !apiKeyHyros}
              >
                {isLoadingConnectHyros ? <CircularProgress color='inherit' size={24} /> : 'Submit'}
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Box>

      {/* Loop Enhancers */}
      <Box marginTop={2}>
        <Paper className={classes.paper}>
          <Typography variant='h5' align='center' gutterBottom>
            Loop Enhancers
          </Typography>

          <List>
            {isLoadingEnhancers ?
              <Skeleton variant='rectangular' height={35} />
              : (enhancers.filter(e => e.platform === 'loop_subscriptions').map(enhancer =>
                <Fragment key={enhancer.id}>
                  <ListItem
                    onClick={() => history.push(ROUTES.LOOP_ENHANCERS + `/${enhancer.id}`)}
                    button
                  >
                    <ListItemIcon>
                      <img
                        className={classes.logoImg}
                        alt='loop_logo'
                        src={loopLogoImg}
                      />
                    </ListItemIcon>
                    <ListItemText primary={enhancer.nickname} secondary={enhancer.platform_id} />
                  </ListItem>
                </Fragment>
              ))
            }
          </List>

          <Box marginTop={2}>
            <Button
              onClick={() => setShowLoopDialog(true)}
              variant='contained'
              color='primary'
            >
              Connect
            </Button>
          </Box>
          <Dialog
            open={showLoopDialog}
            onClose={handleCloseLoopDialog}
          >
            <DialogTitle>
              Connect with Loop Subscriptions
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                The <b>Account ID</b> is used as the ID of your Loop Subscriptions acccount. You can find this in the URL when logged in to Loop.
                The URL is formatted like this: <b>https://ACCOUNT_ID.app.loopwork.co</b>. Enter this ACCOUNT_ID
                value from the URL below. This value is unique to your Loop account.
                <br />
                <br />
                The <b>Account Name</b> is the name of your Loop account. This value can be seen in the top right
                by your profile icon when logged in. Enter this value below.
              </DialogContentText>
              <Box marginTop={2}>
                <TextField
                  label='Account ID'
                  name='loop-account-id'
                  value={accountIdLoop}
                  onChange={(event) => setAccountIdLoop(event.target.value)}
                  variant='outlined'
                  size='small'
                  fullWidth
                  required
                />
              </Box>
              <Box marginTop={2}>
                <TextField
                  label='Account Name'
                  name='loop-account-name'
                  value={accountNameLoop}
                  onChange={(event) => setAccountNameLoop(event.target.value)}
                  variant='outlined'
                  size='small'
                  fullWidth
                  required
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCloseLoopDialog}
                color='error'
              >
                Cancel
              </Button>
              <Button
                onClick={() => connectLoop()}
                color='secondary'
                disabled={!accountIdLoop || !accountNameLoop}
              >
                {isLoadingConnectLoop ? <CircularProgress color='inherit' size={24} /> : 'Submit'}
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Box>

      {/* Recharge Enhancers */}
      <Box marginTop={2}>
        <Paper className={classes.paper}>
          <Typography variant='h5' align='center' gutterBottom>
            Recharge Enhancers
          </Typography>

          <List>
            {isLoadingEnhancers ?
              <Skeleton variant='rectangular' height={35} />
              : (enhancers.filter(e => e.platform === 'recharge').map(enhancer =>
                <Fragment key={enhancer.id}>
                  <ListItem
                    onClick={() => history.push(ROUTES.RECHARGE_ENHANCERS + `/${enhancer.id}`)}
                    button
                  >
                    <ListItemIcon>
                      <img
                        className={classes.logoImg}
                        alt='recharge_logo'
                        src={rechargeLogoImg}
                      />
                    </ListItemIcon>
                    <ListItemText primary={enhancer.nickname} secondary={enhancer.platform_id} />
                  </ListItem>
                </Fragment>
              ))
            }
          </List>

          <Box marginTop={2}>
            <Button
              onClick={() => setShowRechargeDialog(true)}
              variant='contained'
              color='primary'
            >
              Connect
            </Button>
          </Box>
          <Dialog
            open={showRechargeDialog}
            onClose={handleCloseRechargeDialog}
          >
            <DialogTitle>
              Connect with Recharge
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                The <b>Account ID</b> is used as the ID of your Recharge acccount. You can find this in the URL when logged in to Recharge.
                The URL is formatted like this: <b>https://ACCOUNT_ID.admin.rechargeapps.com</b>. Enter this ACCOUNT_ID
                value from the URL below. This value is unique to your Recharge account.
                <br />
                <br />
                The <b>Account Name</b> is the name of your Recharge acccount. This value can be seen in the bottom left
                by your profile icon when logged in, right above your Shopify store URL and the Log out button.
                <br />
                <br />
                The <b>API Key</b> is needed to connect with Recharge. You will first need to create an API Key in your Recharge account
                by navigating to <b>Tools & apps</b> &gt; <b>API tokens</b>. The API Key should have the
                following scopes: <b>read customers</b>, <b>read discounts</b>, <b>read orders</b>, <b>read products</b>, and <b>read subscriptions</b>.
                After creating the API Key, insert the value below.
              </DialogContentText>
              <Box marginTop={2}>
                <TextField
                  label='Account ID'
                  name='recharge-account-id'
                  value={accountIdRecharge}
                  onChange={(event) => setAccountIdRecharge(event.target.value)}
                  variant='outlined'
                  size='small'
                  fullWidth
                  required
                />
              </Box>
              <Box marginTop={2}>
                <TextField
                  label='Account Name'
                  name='recharge-account-name'
                  value={accountNameRecharge}
                  onChange={(event) => setAccountNameRecharge(event.target.value)}
                  variant='outlined'
                  size='small'
                  fullWidth
                  required
                />
              </Box>
              <Box marginTop={2}>
                <TextField
                  label='API Key'
                  name='recharge-api-key'
                  value={apiKeyRecharge}
                  onChange={(event) => setApiKeyRecharge(event.target.value)}
                  variant='outlined'
                  size='small'
                  fullWidth
                  required
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCloseRechargeDialog}
                color='error'
              >
                Cancel
              </Button>
              <Button
                onClick={() => connectRecharge()}
                color='secondary'
                disabled={!accountIdRecharge || !accountNameRecharge || !apiKeyRecharge}
              >
                {isLoadingConnectRecharge ? <CircularProgress color='inherit' size={24} /> : 'Submit'}
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Box>

      {/* Vidalytics Enhancers */}
      <Box marginTop={2}>
        <Paper className={classes.paper}>
          <Typography variant='h5' align='center' gutterBottom>
            Vidalytics Enhancers
          </Typography>

          <List>
            {isLoadingEnhancers ?
              <Skeleton variant='rectangular' height={35} />
              : (enhancers.filter(e => e.platform === 'vidalytics').map(enhancer =>
                <Fragment key={enhancer.id}>
                  <ListItem
                    onClick={() => history.push(ROUTES.VIDALYTICS_ENHANCERS + `/${enhancer.id}`)}
                    button
                  >
                    <ListItemIcon>
                      <img
                        className={classes.logoImg}
                        alt='vidalytics_logo'
                        src={vidalyticsLogoImg}
                      />
                    </ListItemIcon>
                    <ListItemText primary={enhancer.nickname} secondary={enhancer.platform_id} />
                  </ListItem>
                </Fragment>
              ))
            }
          </List>

          <Box marginTop={2}>
            <Button
              onClick={() => setShowVidalyticsDialog(true)}
              variant='contained'
              color='primary'
            >
              Connect
            </Button>
          </Box>
          <Dialog
            open={showVidalyticsDialog}
            onClose={handleCloseVidalyticsDialog}
          >
            <DialogTitle>
              Connect with Vidalytics
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                The <b>API Key</b> is generated from the LTV Numbers integration in Vidalytics at{' '}
                <b>apps.vidalytics.com/integrations</b>. Create the key there and paste it below — we will
                validate it and pull in your Vidalytics account automatically. After connecting, you will
                configure which cart field carries the Vidalytics session id.
              </DialogContentText>
              <Box marginTop={2}>
                <TextField
                  label='API Key'
                  name='vidalytics-api-key'
                  value={apiKeyVidalytics}
                  onChange={(event) => setApiKeyVidalytics(event.target.value)}
                  variant='outlined'
                  size='small'
                  fullWidth
                  required
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCloseVidalyticsDialog}
                color='error'
              >
                Cancel
              </Button>
              <Button
                onClick={() => connectVidalytics()}
                color='secondary'
                disabled={!apiKeyVidalytics || isLoadingConnectVidalytics}
              >
                {isLoadingConnectVidalytics ? <CircularProgress color='inherit' size={24} /> : 'Submit'}
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Box>

      {/* Shopify Journey Enhancers */}
      <Box marginTop={2}>
        <Paper className={classes.paper}>
          <Typography variant='h5' align='center' gutterBottom>
            Shopify Journey Enhancers
          </Typography>

          <List>
            {isLoadingEnhancers ?
              <Skeleton variant='rectangular' height={35} />
              : (enhancers.filter(e => e.platform === 'shopify_journey').map(enhancer =>
                <Fragment key={enhancer.id}>
                  <ListItem
                    onClick={() => history.push(ROUTES.SHOPIFY_JOURNEY_ENHANCERS + `/${enhancer.id}`)}
                    button
                  >
                    <ListItemIcon>
                      <img
                        className={classes.logoImg}
                        alt='shopify_logo'
                        src={shopifyLogoImg}
                      />
                    </ListItemIcon>
                    <ListItemText primary={enhancer.nickname} secondary={enhancer.platform_id} />
                  </ListItem>
                </Fragment>
              ))
            }
          </List>

          <Box marginTop={2}>
            <Button
              onClick={() => setShowShopifyJourneyDialog(true)}
              variant='contained'
              color='primary'
            >
              Connect
            </Button>
          </Box>
          <Dialog
            open={showShopifyJourneyDialog}
            onClose={handleCloseShopifyJourneyDialog}
          >
            <DialogTitle>
              Add a Shopify Journey Enhancer
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                This enhancer reads customer-journey data (landing pages, acquisition channel, campaign, days to purchase,
                and more) from your connected Shopify data sources. No API key is required.
                <br />
                <br />
                Give this enhancer a <b>Name</b> to identify it, then choose which Shopify data sources to enhance on the next screen.
              </DialogContentText>
              <Box marginTop={2}>
                <TextField
                  label='Name'
                  name='shopify-journey-name'
                  value={accountNameShopifyJourney}
                  onChange={(event) => setAccountNameShopifyJourney(event.target.value)}
                  variant='outlined'
                  size='small'
                  fullWidth
                  required
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCloseShopifyJourneyDialog}
                color='error'
              >
                Cancel
              </Button>
              <Button
                onClick={() => connectShopifyJourney()}
                color='secondary'
                disabled={!accountNameShopifyJourney}
              >
                {isLoadingConnectShopifyJourney ? <CircularProgress color='inherit' size={24} /> : 'Submit'}
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Box>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        autoHideDuration={5000}
        onClose={handleCloseMessage}
      >
        <Alert
          severity={messageSeverity}
          variant='filled'
          onClose={handleCloseMessage}
        >
          {message}
        </Alert>
      </Snackbar>
    </Container>
  )
}

export default Enhancers